/**
 * LanguageToggle — smart EN/ES switcher used in nav, footer, and elsewhere.
 *
 * Behavior:
 *  - Writes preference to cookie `lbp_lang` (365d, path=/) + localStorage for redundancy.
 *  - Updates <html lang="..."> via i18n listener.
 *  - If the current URL is a native Spanish page (/es or /es/:slug), toggling to EN
 *    navigates back to `/` (English home). Otherwise toggling just flips i18n in place.
 *  - If on any English page and toggling to ES, stays on the same URL; the component
 *    tree re-renders with Spanish strings where translations are available.
 *
 * Accessibility: uses aria-label + native <button>; focus ring preserved.
 */
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';

export default function LanguageToggle({ className = '', variant = 'nav' }) {
  const { t, i18n } = useTranslation(['nav', 'common']);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = (i18n.language || 'en').split('-')[0];
  const isNativeSpanishRoute = location.pathname === '/es' || location.pathname.startsWith('/es/');
  const targetLang = currentLang === 'es' || isNativeSpanishRoute ? 'en' : 'es';

  const handleToggle = () => {
    // Persist preference early so navigation still reflects it.
    i18n.changeLanguage(targetLang);

    if (targetLang === 'en' && isNativeSpanishRoute) {
      // Native Spanish route → send back to English home. If we had a mapping for
      // individual ES slugs to EN counterparts we'd use it here.
      navigate('/');
    }
    // For all other cases we intentionally stay on the same URL; components re-render
    // with the new language via the useTranslation() hook.
  };

  const label = targetLang === 'es' ? 'ES' : 'EN';
  const tip = t('common:languageToggle.switchTo', {
    lang: targetLang === 'es' ? t('common:languageToggle.spanish') : t('common:languageToggle.english'),
  });

  const base = variant === 'footer'
    ? 'inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white'
    : variant === 'dark'
    ? 'inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white border border-slate-700 rounded-full px-2.5 py-1 bg-slate-900/50'
    : 'inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-600 border border-slate-200 rounded-full px-2.5 py-1';

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`${base} ${className}`}
      aria-label={tip}
      title={tip}
      data-testid="language-toggle"
      data-lang-target={targetLang}
    >
      <Globe className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
